import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from "@mui/material";
import DialogModal from "../dialogModal/DialogModal";
import ErrorMessage from "../errorMessage/ErrorMessage";

import { respondToAvailabilityRequest as apiRespondToAvailabilityRequest } from "../../../services/api";

export default function AvailabilityResponseDialog({
  onClose,
  availabilityRequestPlayer,
  currentPlayerSelected,
}) {
  const [response, setResponse] = useState(
    availabilityRequestPlayer?.isAvailable ?? null
  );
  const [reason, setReason] = useState(availabilityRequestPlayer?.reason || "");
  const [changed, setChanged] = useState(false);
  const [respondingToRequest, setRespondingToRequeset] = React.useState(false);
  const [respondToRequestError, setRespondToRequestError] =
    React.useState(null);

  const respondToAvailabilityRequest = async () => {
    setRespondingToRequeset(true);

    try {
      const respondToRequestResponse = await apiRespondToAvailabilityRequest(
        availabilityRequestPlayer.availabilityRequestId,
        {
          isAvailable: response,
          playerId: availabilityRequestPlayer.player?.id,
          reason,
        }
      );

      if (respondToRequestResponse.success) {
        setRespondingToRequeset(false);
        // Dispatch custom event to notify sidebar to refresh player data
        window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
        onClose();
      } else if (
        respondToRequestResponse.errors &&
        respondToRequestResponse.errors.length > 0
      ) {
        setRespondToRequestError(respondToRequestResponse.errors[0].reason);
        setRespondingToRequeset(false);
      } else {
        setRespondToRequestError("An error occurred responding to request");
        setRespondingToRequeset(false);
      }
    } catch (e) {
      console.log(e);
      setRespondToRequestError(e);
      setRespondingToRequeset(false);
    }
  };

  useEffect(() => {
    // Reset when availabilityRequest changes
    setResponse(availabilityRequestPlayer?.isAvailable ?? null);
    setReason(availabilityRequestPlayer?.reason || "");
    setChanged(false);
  }, [availabilityRequestPlayer]);

  const handleChange = (event, newValue) => {
    setResponse(newValue);
    setChanged(true);
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    setChanged(true);
  };

  const handleSubmit = async () => {
    if (response === null) return;
    await respondToAvailabilityRequest();
    setChanged(false);
  };

  return (
    <DialogModal
      title="Availability Response"
      onClose={() => onClose()}
      cancelText="Close"
      hideCancelButton={false}
      submitting={respondingToRequest}
      submitText="Respond"
      onSubmit={handleSubmit}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 1,
        }}
      >
        {availabilityRequestPlayer && !changed && (
          <Box sx={{ marginLeft: 0 }}>
            {currentPlayerSelected ? "You" : "They"} previously responded:{" "}
            <strong>
              {availabilityRequestPlayer.isAvailable
                ? "Available"
                : "Not Available"}
            </strong>
            {availabilityRequestPlayer.reason &&
              ` — Reason: "${availabilityRequestPlayer.reason}"`}
          </Box>
        )}

        <Typography variant="subtitle1">
          Are {currentPlayerSelected ? "you" : "they"} available?
        </Typography>

        <ToggleButtonGroup
          color="primary"
          value={response}
          exclusive
          onChange={handleChange}
          fullWidth
        >
          <ToggleButton value={true}>Yes</ToggleButton>
          <ToggleButton value={false}>No</ToggleButton>
        </ToggleButtonGroup>

        {response === false && (
          <TextField
            label="Optional reason"
            multiline
            rows={3}
            value={reason}
            onChange={handleReasonChange}
            placeholder="Add a reason (optional)"
          />
        )}

        {respondToRequestError && (
          <ErrorMessage message={respondToRequestError}></ErrorMessage>
        )}
      </Box>
    </DialogModal>
  );
}
